import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { QcRunResponse } from '../api.ts';
import styles from './History.module.css';

interface HistoryProps {
  /** The run currently being viewed — drives the closed trigger (the "selected value"). */
  current: QcRunResponse;
  /** All runs for this contact + suite, shown in the dropdown list. */
  runs: QcRunResponse[];
  /** Switch to a different run (refetches the contact card; does not re-run checks). */
  onSelect: (run: QcRunResponse) => void;
}

const statusDot: Record<string, string> = {
  PASS: '🟢',
  WARN: '🟡',
  FAIL: '🔴',
  ERROR: '🟠',
};

function countByStatus(run: QcRunResponse): { pass: number; warn: number; fail: number } {
  let pass = 0;
  let warn = 0;
  let fail = 0;
  for (const r of run.results) {
    if (r.status === 'PASS') pass++;
    else if (r.status === 'WARN') warn++;
    else if (r.status === 'FAIL' || r.status === 'ERROR') fail++;
  }
  return { pass, warn, fail };
}

function formatWhen(ts: string): string {
  return new Date(ts).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function RunCounts({ run }: { run: QcRunResponse }) {
  const { pass, warn, fail } = countByStatus(run);
  return (
    <span className={styles.counts}>
      {pass > 0 && <span className={styles.countPass}>{pass}✓</span>}
      {warn > 0 && <span className={styles.countWarn}>{warn}!</span>}
      {fail > 0 && <span className={styles.countFail}>{fail}✗</span>}
    </span>
  );
}

/** Subtle marker shown on runs whose results were posted to Forth. */
function PostedMarker({ run }: { run: QcRunResponse }) {
  if (!run.notePosted) return null;
  const when = run.notePostedAt ? ` ${formatWhen(run.notePostedAt)}` : '';
  return (
    <span className={styles.postedMarker} title={`Posted to Forth${when}`} aria-label="Posted to Forth">
      📌
    </span>
  );
}

export function History({ current, runs, onSelect }: HistoryProps) {
  const { t } = useTranslation('ui');
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // History fetch may lag or fail — guarantee the current run is always listed.
  const items = runs.some((r) => r.runId === current.runId) ? runs : [current, ...runs];

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    function onDocMouseDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  function select(run: QcRunResponse) {
    setOpen(false);
    if (run.runId !== current.runId) onSelect(run);
  }

  // Copy a deep link to the currently-selected run.
  function copyCurrentLink() {
    const url = `${window.location.origin}/${current.suiteId}/${current.contactId}/${current.runId}`;
    navigator.clipboard?.writeText(url)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {});
  }

  return (
    <div className={styles.control}>
      <div className={styles.select} ref={ref}>
        <button
          type="button"
          className={styles.trigger}
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={t('history.title', { count: items.length })}
        >
          <span className={styles.dot}>{statusDot[current.overallStatus] ?? '⚪'}</span>
          <span className={styles.when}>{formatWhen(current.timestamp)}</span>
          <RunCounts run={current} />
          <PostedMarker run={current} />
          <span className={styles.chevron} aria-hidden="true">{open ? '▴' : '▾'}</span>
        </button>
        {open && (
          <div className={styles.popover} role="listbox">
            <div className={styles.popoverLabel}>{t('history.title', { count: items.length })}</div>
            {items.map((run) => {
              const isCurrent = run.runId === current.runId;
              return (
                <button
                  key={run.runId}
                  type="button"
                  role="option"
                  aria-selected={isCurrent}
                  className={`${styles.option} ${isCurrent ? styles.optionCurrent : ''}`}
                  onClick={() => select(run)}
                >
                  <span className={styles.dot}>{statusDot[run.overallStatus] ?? '⚪'}</span>
                  <span className={styles.when}>{formatWhen(run.timestamp)}</span>
                  <RunCounts run={run} />
                  <PostedMarker run={run} />
                </button>
              );
            })}
          </div>
        )}
      </div>
      <button
        type="button"
        className={styles.copyButton}
        onClick={copyCurrentLink}
        title={t('history.copyLink')}
        aria-label={t('history.copyLink')}
      >
        {copied ? t('history.copied') : '🔗'}
      </button>
    </div>
  );
}
